import { MRT_SortingState, MRT_TableOptions } from 'material-react-table';
import { toast } from 'react-toastify';

export const useMRTStyling = <TData extends Record<string, any> = any>() => {
  const commonMRTStyling: Partial<MRT_TableOptions<TData>> = {
    // Common table props
    muiTableContainerProps: {
      sx: {
        flex: '1 1 auto',
        maxHeight: 'unset',
        height: '100%',
        overflow: 'auto',
      },
    },
    muiTablePaperProps: {
      sx: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        border: '1px solid rgba(224, 224, 224, 1)',
      },
    },
    muiTableProps: {
      sx: {
        borderRadius: 'inherit',
        marginTop: '0px',
        tableLayout: 'auto',
      },
    },
    muiTopToolbarProps: {
      sx: {
        borderRadius: 'inherit',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        borderRadius: 'inherit',
        border: '1px solid rgba(224, 224, 224, 1)',
      },
    },
    muiTableHeadProps: {
      sx: {
        padding: 0,
        color: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        justifyContent: 'center',
        padding: '12px 9px',
        minHeight: '48px',
        border: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        '&[data-pinned="true"]::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]': {
          zIndex: 10,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
      }),
    },
    initialState: {
      density: 'compact',
    },
    layoutMode: 'grid',
    enableColumnPinning: true,
    enableColumnResizing: true,
    enablePagination: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableColumnActions: false,
    enableSorting: true,
    manualPagination: true,
    manualSorting: true,
    enableTopToolbar: false,
  };

  const getRowStyling = <TRow extends Record<string, any> = any>(row: any) => {
    const backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';

    return {
      muiTableBodyRowProps: ({ row }: { row: any }) => ({
        sx: {
          cursor: 'pointer',
          backgroundColor: backgroundColor,
          '&:hover': {
            backgroundColor: (theme: any) => theme.palette.action.hover,
          },
          '& td[data-pinned="true"]::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: backgroundColor,
            zIndex: -1,
          },
          '& td[data-pinned="true"]': {
            zIndex: 5,
          },
        },
      }),
      muiTableBodyCellProps: ({ row }: { row: any }) => ({
        sx: {
          border: 'none',
          backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
        },
      }),
    };
  };

  const getActionsColumnStyling = () => ({
    muiTableHeadCellProps: {
      align: 'center' as const,
      sx: {
        px: 1,
        py: 0, // match body padding
        backgroundColor: 'primary.main',
        color: '#ffffff',
        justifyContent: 'center',
        padding: '12px 9px',
        minHeight: '48px',
        border: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 10, // Higher z-index for pinned actions column
        '&[data-pinned="true"]::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'primary.main',
          zIndex: 0,
          pointerEvents: 'none',
        },
        '&[data-pinned="true"]': {
          zIndex: 10,
        },
      },
    },
  });

  const getOnSortingChangeWithToast = (currentSorting: MRT_SortingState, setSorting: (next: MRT_SortingState) => void) => {
    return (updater: MRT_SortingState | ((state: MRT_SortingState) => MRT_SortingState)) => {
      const next = typeof updater === 'function' ? (updater as any)(currentSorting) : updater;
      const wasSorted = currentSorting.length > 0;
      setSorting(next);
      if (wasSorted && next.length === 0) {
        toast.info('Sorting cleared – showing default order');
      }
    };
  };

  return {
    commonMRTStyling,
    getRowStyling,
    getActionsColumnStyling,
    getOnSortingChangeWithToast,
  };
};
